// Relationship Radar engine. Pure functions, no DOM, no Node APIs: runs in the browser, in the Worker and in tests.
//
// It encodes Peter's own rules from the Nov 2 call:
//  - Deal screen: no startups or venture, no real estate, funds only by exception; then his five reasons
//    (worth my time, can I do it in the time given, the sponsor has done it before, I get paid, they'll want me back).
//  - Odds of raising against the timeline: about 97% at 18-24 months, 41% at 9, 15% at 4.5, 6% at 3.
//  - A contact hears about a deal only if it is timely and topical and they have the means AND the motivation.
//    Missing any one of those: talk about anything except the deal.
//  - Private life events (a death, illness, divorce, loss of a child) put a person on hold.
//  - Nothing is ever written or sent on his behalf. The engine gives reasons and context, never drafts.

import type { Contact, Deal, DealResponse, Flag, Network, PastPitch, PrivateNote, PrivateState, RecentTouch } from "./network";
import { SENSITIVE_WINDOW_DAYS } from "./network";

/** How long "tapped out" keeps someone from being pitched (the Lists page uses the same window). */
export const TAPPED_OUT_DAYS = 365;

const DAY = 86_400_000;

/** How often Peter talks to someone, in days, by relationship strength (5 = closest). */
export const CADENCE: Record<number, number> = { 5: 45, 4: 90, 3: 180, 2: 365, 1: 730, 0: 100000 };

/** Peter's odds of closing a raise, by months he's given to do it. */
export const ODDS_CURVE: [number, number][] = [[0, 0], [3, 6], [4.5, 15], [9, 41], [18, 97], [24, 98], [60, 98]];

const NON_INVESTOR = new Set(["Banker / advisor", "Service provider"]);

const parseDay = (s: string) => Date.parse(s.slice(0, 10) + "T12:00:00Z");
export const daysBetween = (a: string, b: string): number => Math.round((parseDay(b) - parseDay(a)) / DAY);
export const addDays = (iso: string, n: number): string => new Date(parseDay(iso) + n * DAY).toISOString().slice(0, 10);
export const todayIso = (): string => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export function fmtMoney(v: number): string {
  if (!Number.isFinite(v)) return "$0";
  if (v >= 1e9) return "$" + +(v / 1e9).toFixed(1) + "B";
  if (v >= 1e6) return "$" + +(v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return "$" + Math.round(v / 1e3) + "K";
  return "$" + Math.round(v);
}

export function raiseOdds(months: number): number {
  for (let i = 1; i < ODDS_CURVE.length; i++) {
    const [x0, y0] = ODDS_CURVE[i - 1];
    const [x1, y1] = ODDS_CURVE[i];
    if (months <= x1) return Math.round(y0 + ((months - x0) / (x1 - x0)) * (y1 - y0));
  }
  return 98;
}

/* ---------- 1. Deal screen ---------- */

export type RuleStatus = "pass" | "warn" | "fail";
export interface RuleResult {
  n: number;
  rule: string;
  status: RuleStatus;
  why: string;
}
export interface Screen {
  verdict: "Disqualified" | "Pass" | "Proceed with caution" | "Take it";
  tone: RuleStatus;
  advice: string;
  odds: number;
  disqualifiers: string[];
  rules: RuleResult[];
}

export function screenDeal(d: Deal): Screen {
  const rules: RuleResult[] = [];
  const t = (d.deal_type || "").toLowerCase();
  const disq: string[] = [];
  if (/start-?up|venture|seed|series [ab]/.test(t)) disq.push("Startup or venture. They can't afford you, and it takes a personal reason to care.");
  if (/real estate|property|reit/.test(t)) disq.push("Real estate. It's hyper-local, and your network won't travel for it.");
  if (d.is_fund && !(d.first_time_fund && d.friend_gp)) disq.push("A fund. Only by exception (a new vintage, or a first-time fund run by a friend).");
  const odds = raiseOdds(d.timeline_months || 0);
  const st = (ok: boolean, warn: boolean): RuleStatus => (ok ? "pass" : warn ? "warn" : "fail");
  const values = d.values_flags || [];
  rules.push({
    n: 1,
    rule: "Worth my time",
    status: values.length ? "fail" : "pass",
    why: values.length ? "Conflicts with your values: " + values.join(", ") : "No values conflict flagged.",
  });
  rules.push({
    n: 2,
    rule: "Can I do it in the time given",
    status: st(odds >= 70, odds >= 35),
    why: `${d.timeline_months} months to raise ${fmtMoney(d.raise)}. Your odds at that timeline: about ${odds}%.`,
  });
  rules.push({
    n: 3,
    rule: "Sponsor has raised before",
    status: d.sponsor_prior_raises > 0 ? "pass" : "fail",
    why: d.sponsor_prior_raises > 0 ? `${d.sponsor_prior_raises} prior raises.` : "First time raising capital like this.",
  });
  rules.push({
    n: 4,
    rule: "I get paid",
    status: d.retainer_offered ? "pass" : "fail",
    why: d.retainer_offered ? "Monthly retainer plus 2% offered." : "No retainer mentioned. This isn't charity.",
  });
  const gut = d.culture_gut || 3;
  rules.push({
    n: 5,
    rule: "They'll want me back",
    status: st(gut >= 4, gut === 3),
    why: ["", "Bad feel.", "Bad feel.", "Neutral. Haven't seen enough yet.", "Good people.", "Great people."][gut] ?? "",
  });
  const fails = rules.filter((r) => r.status === "fail").length;
  const warns = rules.filter((r) => r.status === "warn").length;
  let verdict: Screen["verdict"];
  let tone: RuleStatus;
  let advice: string;
  if (disq.length) {
    verdict = "Disqualified";
    tone = "fail";
    advice = "Outside what you raise for.";
  } else if (fails >= 2) {
    verdict = "Pass";
    tone = "fail";
    advice = "Don't take the mandate. To stay helpful, sell the sponsor a curated list instead.";
  } else if (fails === 1 || warns) {
    verdict = "Proceed with caution";
    tone = "warn";
    advice = "Workable if the flagged item gets fixed before you start calling.";
  } else {
    verdict = "Take it";
    tone = "pass";
    advice = "Fits all five. Start with the people below.";
  }
  return { verdict, tone, advice, odds, disqualifiers: disq, rules };
}

/* ---------- 2. A contact's state as of a date ---------- */

export interface StateOptions {
  /** Peter's "contacted" marks, k -> ISO date. Count as an outbound touch on that day. */
  contacted?: Record<string, string>;
  excludeDeal?: string;
  ignoreFlagDates?: boolean;
}

export interface ContactState {
  day: number; // asOf as days since epoch
  daysSince: number | null; // days since the last touch either way
  lastTouch: RecentTouch | null; // the last logged touch before asOf
  flags: Flag[];
  past: PastPitch[];
  unanswered: boolean; // they reached out with a question in the last 30 days and Peter hasn't replied
  touches12: number;
  inbound90: number;
  lastIbDays: number | null;
}

export function epochDay(net: Pick<Network, "epoch">, isoDate: string): number {
  return daysBetween(net.epoch, isoDate);
}

export function stateAsOf(c: Contact, asOf: string, net: Pick<Network, "epoch">, opts: StateOptions = {}): ContactState {
  const day = epochDay(net, asOf);
  const ib = c.ib.filter((x) => x < day);
  const ob = c.ob.filter((x) => x < day);
  const marked = opts.contacted?.[c.k];
  if (marked && marked <= asOf) ob.push(epochDay(net, marked));
  ob.sort((a, b) => a - b);
  const lastIb = ib.length ? ib[ib.length - 1] : null;
  const lastOb = ob.length ? ob[ob.length - 1] : null;
  const last = Math.max(lastIb ?? -1e9, lastOb ?? -1e9);
  const daysSince = last > -1e8 ? day - last : null;
  const flags = c.fl.filter((f) => !f.date || f.date < asOf || opts.ignoreFlagDates);
  const past = c.pa.filter((p) => p.date < asOf && p.deal !== opts.excludeDeal);
  const before = c.rt.filter((t) => t[0] < asOf);
  const lastT = before.length ? before[before.length - 1] : null;
  const unanswered =
    !!lastT &&
    lastT[2] === "I" &&
    lastIb != null &&
    (lastOb == null || lastIb > lastOb) &&
    day - lastIb <= 30 &&
    /\?|asked|invite|dinner|what deals|liquidity|working on/i.test(lastT[3]);
  const touches12 = ib.filter((x) => day - x <= 365).length + ob.filter((x) => day - x <= 365).length;
  const inbound90 = ib.filter((x) => day - x <= 90).length;
  return { day, daysSince, lastTouch: lastT, flags, past, unanswered, touches12, inbound90, lastIbDays: lastIb != null ? day - lastIb : null };
}

export interface Hold {
  why: string;
  kind: string; // "private" or a sensitive flag
  text?: string;
}

const HOLD_LABEL: Record<string, string> = {
  "deceased": "Deceased",
  "loss of child": "Lost a child",
  "serious illness": "Serious illness",
  "divorce": "Going through a divorce",
  "inactive": "Marked inactive",
  "opted out": "Asked not to be contacted",
};
export const holdLabel = (flag: string): string => HOLD_LABEL[flag] ?? flag;

export function holdReason(_c: Contact, s: ContactState, asOf: string, note?: PrivateNote | null): Hold | null {
  if (note?.hold) return { why: "Your private note: " + (note.note || "on hold"), kind: "private" };
  for (const f of s.flags) {
    const win = SENSITIVE_WINDOW_DAYS[f.flag];
    if (win == null) continue;
    const age = f.date ? daysBetween(f.date, asOf) : 0;
    if (age <= win) return { why: holdLabel(f.flag) + (f.date ? ` (${f.date.slice(0, 7)})` : "") + ", from " + f.source, kind: f.flag, text: f.text };
  }
  return null;
}

/* ---------- 3. Score one contact against one deal ---------- */

export type Bucket = "bring" | "reconnect" | "hold" | "responded";

export interface MatchOptions extends StateOptions {
  asOf?: string;
  notes?: Record<string, PrivateNote>;
  responses?: PrivateState["responses"];
}

export interface Gates {
  topical: boolean;
  means: boolean;
  motivation: boolean | "unknown";
  timely: boolean;
}

export interface Match {
  c: Contact;
  s: ContactState;
  bucket: Bucket | null;
  score: number;
  gates: Gates;
  reasons: string[]; // why they fit (green)
  cautions: string[]; // what's against it (amber)
  whyNow: string[]; // why this week (amber)
  hold: Hold | null;
  response?: DealResponse;
}

export function scoreContact(c: Contact, deal: Deal, asOf: string, net: Pick<Network, "epoch">, opts: MatchOptions = {}): Match | null {
  if (NON_INVESTOR.has(c.ty) || !c.cmax) return null;
  const ov = opts.notes?.[c.k];
  const s = stateAsOf(c, asOf, net, opts);
  const reasons: string[] = [];
  const cautions: string[] = [];
  const whyNow: string[] = [];
  const prim = deal.sectors.filter((x) => c.se.includes(x));
  const adj = (deal.adjacent || []).filter((x) => c.se.includes(x));
  const invested = s.past.filter((p) => p.outcome === "Invested").length;
  const passed = s.past.filter((p) => p.outcome === "Passed").length;
  const ownSectors = ov?.sectors ?? [];
  const primAll = prim.concat(deal.sectors.filter((x) => ownSectors.includes(x) && !prim.includes(x)));

  // Topical
  let topical = primAll.length > 0 || (adj.length > 0 && invested > 0);
  if (primAll.length) reasons.push("Invests in " + primAll.join(" & "));
  else if (adj.length && topical) reasons.push("Adjacent: " + adj.join(", ") + ", and has invested with you");
  const burned = (c.bu || []).filter((b) => deal.sectors.includes(b));
  if (burned.length) {
    topical = false;
    cautions.push("Got burned on a " + burned[0] + " deal; gun-shy there");
  }
  if (c.ty === "LP / fund allocator" && !deal.is_fund) {
    topical = false;
    cautions.push("Only does funds");
  }
  if (c.dt.length && deal.structure && !c.dt.includes(deal.structure)) cautions.push("Usually doesn't do " + deal.structure.toLowerCase());

  // Means
  const means = c.cmax >= deal.min_ticket && c.cmin <= deal.raise * 0.3;
  if (means) reasons.push(`Checks ${fmtMoney(c.cmin)}-${fmtMoney(c.cmax)}` + (c.cs !== "crm" ? ` (${c.cs})` : ""));
  else cautions.push(c.cmax < deal.min_ticket ? `Checks top out at ${fmtMoney(c.cmax)}, below the ${fmtMoney(deal.min_ticket)} minimum` : "Too big for this deal");

  // Motivation
  let motive = 0;
  const flagset = new Set(s.flags.map((f) => f.flag));
  if (invested) {
    motive += 2;
    reasons.push(`Invested with you ${invested}x before`);
  }
  const askedRecently = (c.as ?? []).some((a) => deal.sectors.includes(a.sector) && a.date && a.date < asOf && daysBetween(a.date, asOf) <= 180);
  if (s.inbound90 || askedRecently) {
    const askedDeals =
      askedRecently || c.rt.some((t) => t[2] === "I" && /deal|working on|liquidity/i.test(t[3]) && t[0] < asOf && daysBetween(t[0], asOf) <= 120);
    motive += askedDeals ? 2 : 1;
    if (askedRecently) whyNow.push(`Asked you for ${deal.sectors.filter((x) => (c.as ?? []).some((a) => a.sector === x))[0]} deals recently`);
    else if (askedDeals) whyNow.push("Recently asked what deals you're seeing");
  }
  if (flagset.has("sold company")) {
    motive += 2;
    whyNow.push("Sold a company; has cash to redeploy");
  }
  const likes = !!c.no && primAll.some((x) => c.no.toLowerCase().includes(x.toLowerCase().split(" ")[0]));
  if (likes) motive += 1;
  if (c.rs >= 4) motive += 1;
  if (passed >= 2) {
    motive -= 1;
    cautions.push(`Passed on ${passed} of your past deals`);
  }
  const respPast = opts.responses
    ? Object.entries(opts.responses).filter(([did, m]) => did !== deal.deal_id && m[c.k] === "passed").length
    : 0;
  if (respPast) {
    motive -= respPast;
    cautions.push(`Passed on ${respPast} deal${respPast > 1 ? "s" : ""} you logged here`);
  }
  let motivation: Gates["motivation"] = motive >= 3 && primAll.length > 0 ? true : motive >= 1 ? "unknown" : false;

  // Timely (also where "just invested" and "retired" live)
  let timely = true;
  if (flagset.has("retired")) {
    timely = false;
    motivation = false;
    cautions.push("Says they're done with private deals or retired");
  }
  // Tapped out: no money to put in right now, for a year from when they said so (same window as the Lists page).
  // Still worth keeping warm, so they land in "talk about anything else", not off the list.
  const tapped = s.flags.find((f) => f.flag === "tapped out");
  if (tapped && (!tapped.date || daysBetween(tapped.date, asOf) <= TAPPED_OUT_DAYS)) {
    timely = false;
    cautions.push(`Said they're tapped out${tapped.date ? ` (${tapped.date.slice(0, 7)})` : ""}`);
  }
  if (c.ji && c.ji.date && c.ji.date < asOf && daysBetween(c.ji.date, asOf) <= 180) {
    const same = deal.sectors.includes(c.ji.sector);
    if (same) {
      timely = false;
      cautions.push(`Just put money into a ${c.ji.sector} deal (${c.ji.date.slice(0, 7)}); doesn't need another`);
    } else cautions.push(`Wrote a check elsewhere in ${c.ji.date.slice(0, 7)}`);
  }
  const upcomingConf = (c.cf || []).filter((e) => e.date >= asOf && daysBetween(asOf, e.date) <= 45);
  if (upcomingConf.length) whyNow.push(`You'll both be at ${upcomingConf[0].name} (${upcomingConf[0].date})`);
  if (s.unanswered) whyNow.push(`Reached out ${s.lastIbDays} days ago and hasn't heard back`);
  if (c.lco) whyNow.push(`LinkedIn shows a new firm: ${c.lco}`);
  if (ov?.noPitch) {
    timely = false;
    cautions.push("Your note: don't pitch right now");
  }

  const hold = holdReason(c, s, asOf, ov);

  // Rank score
  let score = 0;
  score += (22 * Math.min(primAll.length, 2)) / 2 + (adj.length ? 5 : 0);
  score += 4 * c.rs;
  score += !c.dt.length || c.dt.includes(deal.structure) ? 6 : -6;
  score += invested ? 12 + 3 * Math.min(invested, 3) : 0;
  score += Math.min(motive, 6) * 3;
  score += s.unanswered ? 6 : 0;
  score += upcomingConf.length ? 4 : 0;
  if (c.cs !== "crm") score -= 3;
  if (s.daysSince != null && s.daysSince > 3 * CADENCE[c.rs || 1]) score -= 3;
  const response = opts.responses?.[deal.deal_id]?.[c.k];

  let bucket: Bucket | null;
  if (hold) bucket = "hold";
  else if (topical && means && timely && motivation === true) bucket = "bring";
  else if (topical && means && motivation !== false) bucket = "reconnect";
  else if (topical && means && !timely) bucket = "reconnect";
  else bucket = null;
  if (bucket === "bring" && response === "passed") bucket = "responded";

  return { c, s, bucket, score: Math.round(score * 10) / 10, gates: { topical, means, motivation, timely }, reasons, cautions, whyNow, hold, response };
}

export interface DealMatches {
  bring: Match[];
  reconnect: Match[];
  hold: Match[];
  responded: Match[];
  counts: Record<Bucket, number>;
}

export function matchDeal(net: Network, deal: Deal, opts: MatchOptions = {}): DealMatches {
  const asOf = opts.asOf || net.generated;
  const out: DealMatches = { bring: [], reconnect: [], hold: [], responded: [], counts: { bring: 0, reconnect: 0, hold: 0, responded: 0 } };
  for (const c of net.contacts) {
    const r = scoreContact(c, deal, asOf, net, opts);
    if (!r || !r.bucket) continue;
    // Only hold people who would otherwise fit, unless Peter put them on hold himself.
    if (r.bucket === "hold" && !(r.gates.topical && r.gates.means) && r.hold?.kind !== "private") continue;
    out[r.bucket].push(r);
  }
  const byScore = (a: Match, b: Match) => Number(b.response === "interested") - Number(a.response === "interested") || b.score - a.score;
  for (const k of ["bring", "reconnect", "hold", "responded"] as const) {
    out[k].sort(byScore);
    out.counts[k] = out[k].length;
  }
  return out;
}

/* ---------- 4. Who's overdue, and why now (no deal needed) ---------- */

export interface RadarOptions extends StateOptions {
  asOf?: string;
  notes?: Record<string, PrivateNote>;
}

export interface RadarRow {
  c: Contact;
  s: ContactState;
  why: string[];
  score: number;
  personal: boolean; // a personal check-in only, no business
  hold: Hold | null;
  ratio: number; // days since contact / usual cadence
  cadence: number;
}

export function radar(net: Network, opts: RadarOptions = {}): RadarRow[] {
  const asOf = opts.asOf || net.generated;
  const rows: RadarRow[] = [];
  for (const c of net.contacts) {
    if (!c.rs || c.rs < 2) continue;
    const ov = opts.notes?.[c.k];
    const s = stateAsOf(c, asOf, net, opts);
    const hold = holdReason(c, s, asOf, ov);
    if (hold && (hold.kind === "deceased" || hold.kind === "inactive" || hold.kind === "opted out")) continue;
    const cad = CADENCE[c.rs];
    const ratio = s.daysSince == null ? 3 : s.daysSince / cad;
    const why: string[] = [];
    let pri = 0;
    if (s.unanswered && !hold) {
      why.push(`Reached out ${s.lastIbDays} days ago, no reply yet`);
      pri += 50;
    }
    const recent = (f: string) => s.flags.find((x) => x.flag === f && x.date && daysBetween(x.date, asOf) <= 240);
    if (recent("sold company")) {
      why.push("Sold a company recently, so may have cash to put to work");
      pri += 20;
    }
    if (recent("new role")) {
      why.push("Started a new role");
      pri += 12;
    }
    if (recent("family milestone")) {
      why.push("Family milestone");
      pri += 8;
    }
    if (c.lco) {
      why.push(`LinkedIn now says ${c.lco}. Changed jobs?`);
      pri += 6;
    }
    const conf = (c.cf || []).filter((e) => e.date >= asOf && daysBetween(asOf, e.date) <= 45);
    if (conf.length) {
      why.push(`You're both at ${conf[0].name} on ${conf[0].date}`);
      pri += 10;
    }
    if (ratio >= 2.5 && c.rs >= 4) {
      why.push(`Close relationship (${c.rs}/5), quiet for ${s.daysSince ?? "?"} days`);
      pri += 15;
    } else if (ratio >= 3) {
      why.push(`No contact in ${s.daysSince ?? "?"} days (usually every ~${cad})`);
      pri += 5;
    }
    const personal = !!hold && (hold.kind === "serious illness" || hold.kind === "divorce" || hold.kind === "loss of child" || hold.kind === "private");
    if (hold && hold.kind !== "private" && personal) {
      why.unshift("Personal check-in only, no business: " + hold.why.split(", from")[0]);
      pri += 25;
    }
    if (!why.length) continue;
    const score = pri + (Math.min(ratio, 4) * 4 * c.rs) / 3 + c.rs * 2;
    rows.push({ c, s, why, score: Math.round(score), personal, hold, ratio: Math.round(ratio * 10) / 10, cadence: cad });
  }
  rows.sort((a, b) => b.score - a.score);
  return rows;
}

/* ---------- 5. Backtest: would past investors have made the list, using only what was known then? ---------- */

export interface BacktestRow {
  deal_id: string;
  name: string;
  launch: string;
  investors: number;
  eligible: number;
  ranked: number;
  hits: Record<number, number>;
  random: Record<number, number>;
}

export function backtest(net: Network, topK: number[] = [25, 50]): BacktestRow[] {
  const res: BacktestRow[] = [];
  const eligible = net.contacts.filter((c) => !NON_INVESTOR.has(c.ty) && c.cmax).length;
  for (const pd of net.past_deals) {
    if (!pd.launch_date || pd.launch_date < "2021-01-01") continue; // needs history before launch
    const deal: Deal = {
      deal_id: pd.deal_id, codename: pd.name, received: pd.launch_date, one_liner: "", summary: "", sponsor: pd.sponsor,
      deal_type: "", structure: pd.structure, sectors: pd.sectors.split("|").filter(Boolean), adjacent: [], raise: +pd.raise_usd || 0,
      min_ticket: 250000, region: "", timeline_months: 12, sponsor_prior_raises: 1, retainer_offered: true, first_time_fund: false,
      is_fund: false, values_flags: [], culture_gut: 4,
    };
    const asOf = pd.launch_date;
    const scored: { k: string; score: number }[] = [];
    for (const c of net.contacts) {
      const r = scoreContact(c, deal, asOf, net, { excludeDeal: pd.deal_id });
      if (!r || !r.bucket || r.bucket === "hold") continue;
      scored.push({ k: c.k, score: r.score }); // rank on score alone: motivation signals weren't recorded historically
    }
    scored.sort((a, b) => b.score - a.score);
    const actual = new Set(net.contacts.filter((c) => c.pa.some((p) => p.deal === pd.deal_id && p.outcome === "Invested")).map((c) => c.k));
    const row: BacktestRow = { deal_id: pd.deal_id, name: pd.name, launch: asOf, investors: actual.size, eligible, ranked: scored.length, hits: {}, random: {} };
    for (const k of topK) {
      row.hits[k] = scored.slice(0, k).filter((x) => actual.has(x.k)).length;
      row.random[k] = eligible ? +((actual.size * k) / eligible).toFixed(2) : 0;
    }
    res.push(row);
  }
  return res;
}
